/**
 * Iter XX.94 — Parent Entry Gate.
 *
 * Iter XX.106 — Three-step interstitial (was two-step). The
 * "I listen. I remember. I coach." explanation screen in Chat.jsx
 * was removed; a minimal identity screen slots in here instead so
 * the transition from audience-picker to chat still has ONE
 * moment of intent, without any product-explanation copy:
 *
 *   Step 1: "Who are we helping today?"          →  Me / My child
 *   Step 2 (only if My child):  "What's your child's first name?"
 *   Step 3 (always):  minimal identity + Continue → opens chat
 *
 * No forms, no email, no AI claims, no memory claims — the founder
 * brief for XX.106 is explicit: let the first conversation prove
 * what Gully does, don't describe it.
 */
import { useEffect, useState } from "react";
import { markParentGateCompleted } from "../lib/parentEntryGate";
import { pcePreload, RECOGNITION_LINE_1, RECOGNITION_LINE_2 } from "../lib/progressiveCommitmentEngine";
import { track } from "../lib/clarity";
import api from "../lib/api";

// Iter XX.106 — Identity-screen copy. Two sentences. No product
// explanation, no AI claim, no memory claim. Only recognition of
// effort + a promise to make it count. Locked here so a future
// change is deliberate.
// Iter XX.133 — copy hoisted to progressiveCommitmentEngine.js so
// both ParentEntryGate AND the eager-PCE fallback (fresh anon
// without a gate) share the same canonical lines.
const IDENTITY_LINE_1 = RECOGNITION_LINE_1;
const IDENTITY_LINE_2 = RECOGNITION_LINE_2;

function _emit(event, payload, sessionId) {
  try { track(event, payload || {}); } catch { /* noop */ }
  try {
    if (sessionId) {
      api.post("/web/track", {
        session_id: sessionId,
        event,
        payload: payload || {},
      }).catch(() => {});
    }
  } catch { /* noop */ }
}

/**
 * @param {object}   props
 * @param {string}   props.sessionId  Chat session id — used for /web/track.
 * @param {function} props.onComplete Called with `{ relationship, player_name? }` once done.
 * @param {string=}  props.variant    "first_frame" (Iter XX.98 parent-first
 *                                    onboarding, gate shown before the hero)
 *                                    or "commitment" (XX.96 default — gate
 *                                    fires after chip/Send). Purely an
 *                                    analytics label; UI is identical.
 */
export default function ParentEntryGate({ sessionId, onComplete, variant }) {
  const gateVariant = variant === "first_frame" ? "first_frame" : "commitment";
  // Iter XX.133 — removed the "identity" step. Two steps only now:
  //   mode → name (parent only) → onComplete.
  const [step, setStep] = useState("mode"); // mode | name
  const [name, setName] = useState("");
  const [advancing, setAdvancing] = useState(false);

  useEffect(() => {
    _emit("parent_gate_shown", { step: "mode", variant: gateVariant }, sessionId);
    // Iter XX.133 — stash the recognition copy so QuestionEngineCard
    // can render it as a warm intro line above the first question.
    // This preserves the founder-authored recognition line without
    // requiring a Continue-button screen.
    try {
      sessionStorage.setItem("g_pce_intro_line_1", IDENTITY_LINE_1);
      sessionStorage.setItem("g_pce_intro_line_2", IDENTITY_LINE_2);
    } catch { /* noop */ }
  }, [sessionId, gateVariant]);

  function _pickSelf() {
    if (advancing) return;
    _emit("entry_mode_selected", { mode: "self", variant: gateVariant }, sessionId);
    _emit("player_entry_selected", { variant: gateVariant }, sessionId);
    // Iter XX.133 — removed the decision-less "identity" Continue
    // screen. The audience choice is itself the durable persistence
    // signal AND the intent to enter chat. The recognition copy
    // ("You wouldn't be here if cricket didn't matter to you.")
    // is now merged into the very first PCE question card as a
    // warm intro line — one continuous conversation, no interstitial
    // Continue button.
    markParentGateCompleted({ relationship: "self" });
    setAdvancing(true);
    // Iter XX.133 — kick off /api/pce/start NOW so it lands before
    // the QuestionEngineCard mounts. Zero visible loading state.
    try { pcePreload(sessionId); } catch { /* noop */ }
    setTimeout(() => onComplete?.({ relationship: "self" }), 80);
  }

  function _pickParent() {
    if (advancing) return;
    _emit("entry_mode_selected", { mode: "parent", variant: gateVariant }, sessionId);
    setStep("name");
    _emit("parent_gate_shown", { step: "name", variant: gateVariant }, sessionId);
  }

  function _submitName(e) {
    e?.preventDefault?.();
    if (advancing) return;
    const trimmed = name.trim();
    if (!trimmed) return;
    const first = trimmed.split(/\s+/)[0].slice(0, 40);
    _emit("parent_name_captured",
          { name_length: first.length, variant: gateVariant }, sessionId);
    _emit("parent_entry_selected",
          { variant: gateVariant, name_length: first.length }, sessionId);
    // Iter XX.133 — same as _pickSelf: no interstitial identity
    // Continue screen. Name capture IS the intent to continue.
    markParentGateCompleted({ relationship: "parent", player_name: first });
    setAdvancing(true);
    // Iter XX.133 — preload PCE so the first question is instant.
    try { pcePreload(sessionId); } catch { /* noop */ }
    setTimeout(
      () => onComplete?.({ relationship: "parent", player_name: first }),
      80,
    );
  }

  return (
    <div
      data-testid="parent-entry-gate"
      className="fixed inset-0 z-50 bg-neutral-950 overflow-y-auto flex flex-col items-stretch"
    >
      <div className="w-full max-w-md mx-auto px-5 pt-14 pb-10 flex-1 flex flex-col">
        <div className="mb-8">
          <h1
            className="text-3xl sm:text-4xl font-semibold text-neutral-100 leading-tight"
            data-testid="parent-entry-gate-title"
          >
            {step === "mode" && "Who are we helping today?"}
            {step === "name" && "What's your child's first name?"}
          </h1>
          {step === "name" && (
            <p className="mt-2 text-neutral-400 text-base">
              Just a first name is fine — I&apos;ll use it naturally in our chats.
            </p>
          )}
        </div>

        {step === "mode" && (
          <div className="space-y-3" data-testid="parent-entry-gate-mode-step">
            <button
              type="button"
              onClick={_pickSelf}
              data-testid="parent-entry-gate-mode-self"
              disabled={advancing}
              className="w-full flex items-start gap-4 rounded-2xl border border-neutral-800 bg-neutral-900/70 px-5 py-5 text-left text-neutral-100 hover:border-emerald-700 hover:bg-neutral-900 active:scale-[0.99] disabled:opacity-60 transition-all"
            >
              <span aria-hidden="true" className="text-3xl">🏏</span>
              <span>
                <span className="block text-lg font-medium">Me</span>
                <span className="block text-sm text-neutral-400 mt-1">
                  For my own cricket.
                </span>
              </span>
            </button>
            <button
              type="button"
              onClick={_pickParent}
              data-testid="parent-entry-gate-mode-parent"
              disabled={advancing}
              className="w-full flex items-start gap-4 rounded-2xl border border-neutral-800 bg-neutral-900/70 px-5 py-5 text-left text-neutral-100 hover:border-emerald-700 hover:bg-neutral-900 active:scale-[0.99] disabled:opacity-60 transition-all"
            >
              <span aria-hidden="true" className="text-3xl">❤️</span>
              <span>
                <span className="block text-lg font-medium">My child</span>
                <span className="block text-sm text-neutral-400 mt-1">
                  Help me understand and support my child&apos;s cricket.
                </span>
              </span>
            </button>
          </div>
        )}

        {step === "name" && (
          <form
            onSubmit={_submitName}
            className="space-y-4"
            data-testid="parent-entry-gate-name-step"
          >
            <input
              type="text"
              autoFocus
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="E.g. Aarav"
              maxLength={40}
              data-testid="parent-entry-gate-name-input"
              className="w-full rounded-2xl border border-neutral-800 bg-neutral-900/70 px-5 py-4 text-lg text-neutral-100 placeholder:text-neutral-500 focus:border-emerald-700 focus:outline-none"
            />
            <button
              type="submit"
              disabled={advancing || !name.trim()}
              data-testid="parent-entry-gate-name-submit"
              className="w-full rounded-2xl bg-emerald-700 px-5 py-4 text-lg font-medium text-white hover:bg-emerald-600 active:scale-[0.99] disabled:opacity-50 transition-all"
            >
              Continue
            </button>
            <button
              type="button"
              onClick={() => { setStep("mode"); setName(""); }}
              disabled={advancing}
              data-testid="parent-entry-gate-name-back"
              className="text-sm text-neutral-500 hover:text-neutral-300 underline underline-offset-4 disabled:opacity-40"
            >
              ← Back
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
